import { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AuthShell from "./AuthShell";
import { api } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import CountryPhoneInput from "@/components/common/CountryPhoneInput";

const PASSWORD_REGEX = /^(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
const USERNAME_REGEX = /^[a-zA-Z0-9_.-]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TITLE_OPTIONS = [
  { value: "", label: "Select title" },
  { value: "Mr", label: "Mr" },
  { value: "Mrs", label: "Mrs" },
  { value: "Ms", label: "Ms" },
  { value: "Dr", label: "Dr" },
  { value: "Prof", label: "Prof" },
  { value: "Other", label: "Other" },
];

const REFERRAL_OPTIONS = [
  "Select an option",
  "Google Search",
  "LinkedIn",
  "Facebook",
  "Instagram",
  "WhatsApp",
  "Friend or Colleague",
  "Company Referral",
  "Other",
];

const FIELDS = [
  ["username", "Username", "text", true, AUTH.registerUsername],
  ["email", "Email address", "email", true, AUTH.registerEmail],
  ["mobile", "Mobile number", "tel", true, AUTH.registerMobile],
  ["organization", "Organization", "text", true, AUTH.registerOrganization],
  ["affiliation", "Affiliation", "text", false, AUTH.registerAffiliation],
  ["city", "City", "text", true, AUTH.registerCity],
  ["state", "State", "text", true, AUTH.registerState],
  ["country", "Country", "text", true, AUTH.registerCountry],
  ["referral_source", "How did you hear about us?", "select", false, AUTH.registerReferral],
  ["password", "Password", "password", true, AUTH.registerPassword],
  ["confirm_password", "Confirm password", "password", true, AUTH.registerPassword],
];

export default function RegisterPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "", first_name: "", last_name: "", username: "", email: "", mobile: "",
    organization: "", affiliation: "", city: "", state: "", country: "",
    referral_source: "", password: "", confirm_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;

    if (!formEl.checkValidity()) {
      formEl.reportValidity();
      return;
    }

    if (!PASSWORD_REGEX.test(form.password)) {
      toast.error("Password must be at least 8 characters and include at least 1 number and 1 symbol.");
      return;
    }

    if (form.password !== form.confirm_password) {
      toast.error("Passwords do not match.");
      return;
    }

    const title = (form.title || "").trim();
    const firstName = (form.first_name || "").trim();
    const lastName = (form.last_name || "").trim();
    const username = (form.username || "").trim().toLowerCase();
    const email = (form.email || "").trim().toLowerCase();
    const organization = (form.organization || "").trim();
    const affiliation = (form.affiliation || "").trim();
    const city = (form.city || "").trim();
    const state = (form.state || "").trim();
    const country = (form.country || "").trim();
    const referralSource = (form.referral_source || "").trim();
    const mobileValue = (form.mobile || "").trim().replace(/\s+/g, "");

    if (firstName.length < 2) {
      toast.error("Please enter your first name.");
      return;
    }

    if (lastName.length < 2) {
      toast.error("Please enter your last name.");
      return;
    }

    if (username.length < 3 || !USERNAME_REGEX.test(username)) {
      toast.error("Username must be 3-40 characters and contain only letters, numbers, dots, underscores, or hyphens.");
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (organization.length > 200) {
      toast.error("Organization name is too long.");
      return;
    }

    if (affiliation.length > 200) {
      toast.error("Affiliation is too long.");
      return;
    }

    if (city.length > 120 || state.length > 120 || country.length > 120) {
      toast.error("City, state, and country must be shorter than 120 characters each.");
      return;
    }

    if (referralSource.length > 120) {
      toast.error("Referral source is too long.");
      return;
    }

    if (!/^\+91[6-9]\d{9}$/.test(mobileValue)) {
      toast.error("Please enter a valid 10-digit Indian mobile number in international format (for example +919876543210).");
      return;
    }

    const payload = {
      ...form,
      full_name: [title, firstName, lastName].filter(Boolean).join(" ").trim(),
      title,
      first_name: firstName,
      last_name: lastName,
      username,
      email,
      organization,
      affiliation,
      city,
      state,
      country,
      referral_source: referralSource,
      mobile: mobileValue,
    };
    delete payload.confirm_password;

    setSubmitting(true);
    try {
      await api.post("/auth/register", payload);
      toast.success("Registration successful. Please check your email for the OTP.");
      nav(`/verify-email?email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string" && detail.toLowerCase().includes("already registered")) {
        toast.error("An account with this email or username already exists. Please sign in or use a different one.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Registration failed");
      }
    } finally { setSubmitting(false); }
  };

  const renderField = (k, label, type, req, tid) => {
    const isMobile = k === "mobile";
    const isPassword = k === "password";
    const isConfirmPassword = k === "confirm_password";
    const isPasswordField = isPassword || isConfirmPassword;
    const isReferral = k === "referral_source";
    return (
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1.5">
          {label}{req && label !== "Affiliation" && <span className="text-[#DC2626]"> *</span>}
        </label>
        {isReferral ? (
          <select
            data-testid={tid}
            value={form[k]}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
          >
            {REFERRAL_OPTIONS.map((option) => (
              <option key={option} value={option === "Select an option" ? "" : option}>
                {option}
              </option>
            ))}
          </select>
        ) : (
          <div className="relative">
            {isMobile ? (
              <CountryPhoneInput
                data-testid={tid}
                required={req}
                value={form[k]}
                onChange={(value) => setForm({ ...form, [k]: value })}
                className="w-full"
              />
            ) : (
              <input
                data-testid={tid}
                type={isPasswordField ? (isPassword ? (showPassword ? "text" : "password") : (showConfirmPassword ? "text" : "password")) : type}
                required={req}
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                minLength={isPasswordField ? 8 : undefined}
                pattern={isPasswordField ? "^(?=.*\\d)(?=.*[^A-Za-z0-9]).{8,}$" : undefined}
                title={isPasswordField ? "Password must be at least 8 characters and include at least 1 number and 1 symbol" : undefined}
                className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
              />
            )}
            {isPasswordField ? (
              <button
                type="button"
                onClick={() => (isPassword ? setShowPassword((v) => !v) : setShowConfirmPassword((v) => !v))}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-gray-500 hover:text-gray-700"
                aria-label={isPassword ? (showPassword ? "Hide password" : "Show password") : (showConfirmPassword ? "Hide confirm password" : "Show confirm password")}
              >
                {isPassword ? (showPassword ? <EyeOff size={18} /> : <Eye size={18} />) : (showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />)}
              </button>
            ) : null}
          </div>
        )}
      </div>
    );
  };

  return (
    <AuthShell title="Create your account" subtitle="Start submitting jobs in under 5 minutes">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Title<span className="text-[#DC2626]"> *</span>
            </label>
            <select
              data-testid={AUTH.registerTitle}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              className="w-full min-w-0 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
            >
              {TITLE_OPTIONS.map((option) => (
                <option key={option.value || option.label} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              First name<span className="text-[#DC2626]"> *</span>
            </label>
            <input
              data-testid={AUTH.registerFirstName}
              type="text"
              required
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
            />
          </div>
          <div className="md:col-span-5">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Last name<span className="text-[#DC2626]"> *</span>
            </label>
            <input
              data-testid={AUTH.registerLastName}
              type="text"
              required
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135] transition-colors"
            />
          </div>
        </div>

        {FIELDS.filter(([k]) => !["title", "first_name", "last_name"].includes(k)).map(([k, label, type, req, tid], index, arr) => {
          if (k === "city") {
            const stateField = arr.find(([fieldKey]) => fieldKey === "state");
            if (!stateField) return null;
            const [, stateLabel, stateType, stateReq, stateTid] = stateField;
            return (
              <div key="city-state-row" className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>{renderField(k, label, type, req, tid)}</div>
                <div>{renderField("state", stateLabel, stateType, stateReq, stateTid)}</div>
              </div>
            );
          }
          if (k === "state") return null;
          return <div key={k}>{renderField(k, label, type, req, tid)}</div>;
        })}
        <button
          data-testid={AUTH.registerSubmit}
          disabled={submitting}
          className="w-full bg-[#0B6936] text-white hover:bg-[#09582D] transition-colors rounded-md px-4 py-3 text-sm font-semibold disabled:opacity-60"
        >
          {submitting ? "Creating account…" : "Create account"}
        </button>
        <p className="text-sm text-gray-600 text-center">
          Already have an account?{" "}
          <Link to="/login" className="text-[#0B6936] hover:text-[#09582D] font-semibold">Sign in</Link>
        </p>
      </form>
    </AuthShell>
  );
}
